/**
 * Sign layout: every signpost decision that needs no GPU or DOM — plate widths,
 * ellipsizing, atlas packing, the per-kind style constants. Two invariants: the
 * atlas cell's aspect IS the plate's aspect (`cellPx` for `plateHeightM` is the
 * style's pixels-per-metre, so a plate can never stretch its texture), and text
 * is measured with the font it is drawn with (`styleFont`; `planPlate` takes
 * the measuring function).
 */

export type SignKind = "street" | "poi";

/**
 * Air between the top of the post and the plate's bottom edge, metres — the
 * post is fatter than the plate is thick, so it ends just under the plate and
 * the plate caps it. Five millimetres is the canonical "not touching" gap.
 */
export const PLATE_MOUNT_GAP_M = 0.005;

export interface SignStyle {
  /** Post cross-section, metres (square). */
  postWidthM: number;
  /**
   * Ground to top of the post, metres — always `PLATE_MOUNT_GAP_M` below the
   * plate's bottom edge (asserted in the style test).
   */
  postHeightM: number;
  /** Height of the plate itself, metres. */
  plateHeightM: number;
  /** How thick the board is — the part you see edge-on. */
  plateThicknessM: number;
  /** Height of the plate's centre above the post's base, metres. */
  plateCenterYM: number;
  /** A plate is never narrower than this, however short the text. */
  minWidthM: number;
  /** …and never wider than this; text that will not fit is ellipsized. */
  maxWidthM: number;
  /** Cell height in atlas pixels. With `plateHeightM` this fixes px-per-metre. */
  cellPx: number;
  /** Glyph size in atlas pixels. */
  fontPx: number;
  /** Horizontal breathing room either side of the text, atlas pixels. */
  padPx: number;
  /** Border/accent strip thickness, atlas pixels. */
  borderPx: number;
  /** CSS font stack (without the size — `styleFont` assembles the shorthand). */
  fontWeight: number;
  fontFamily: string;
  /** Vertical centre of the text within the cell, as a fraction of `cellPx`. */
  baselineFrac: number;
}

/**
 * Austrian street plates: deep blue enamel, white border, white capitals. Place
 * boards: a larch plank with cream lettering, half again as tall and lower. The
 * two read as different OBJECTS at 30 m.
 */
export const SIGN_STYLES: Record<SignKind, SignStyle> = {
  street: {
    postWidthM: 0.08,
    postHeightM: 2.235, // plateCenterYM − plateHeightM/2 − PLATE_MOUNT_GAP_M
    plateHeightM: 0.3,
    plateThicknessM: 0.03,
    plateCenterYM: 2.39,
    minWidthM: 0.55,
    maxWidthM: 2.2,
    cellPx: 48,
    fontPx: 27,
    padPx: 15,
    borderPx: 2,
    fontWeight: 700,
    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
    baselineFrac: 0.53,
  },
  poi: {
    postWidthM: 0.09,
    postHeightM: 1.97, // plateCenterYM − plateHeightM/2 − PLATE_MOUNT_GAP_M
    plateHeightM: 0.45,
    plateThicknessM: 0.035,
    plateCenterYM: 2.2,
    minWidthM: 0.7,
    maxWidthM: 2.2,
    cellPx: 64,
    fontPx: 32,
    padPx: 18,
    borderPx: 3,
    fontWeight: 600,
    fontFamily: 'Georgia, "Times New Roman", serif',
    baselineFrac: 0.47,
  },
};

/** The CSS font shorthand for a style — measuring and drawing MUST share it. */
export function styleFont(style: SignStyle): string {
  return `${style.fontWeight} ${style.fontPx}px ${style.fontFamily}`;
}

/** Atlas pixels per world metre for a style. */
export function pxPerMetre(style: SignStyle): number {
  return style.cellPx / style.plateHeightM;
}

const ELLIPSIS = "…";

/**
 * Trims `text` until it fits `maxPx`, marking the cut with an ellipsis — a
 * plate keeps its proportions and loses characters. The bare ellipsis when
 * nothing fits.
 */
export function ellipsize(
  text: string,
  maxPx: number,
  measure: (s: string) => number,
): string {
  if (measure(text) <= maxPx) return text;
  const chars = Array.from(text);
  for (let n = chars.length - 1; n > 0; n--) {
    const cand = chars.slice(0, n).join("").trimEnd() + ELLIPSIS;
    if (measure(cand) <= maxPx) return cand;
  }
  return ELLIPSIS;
}

export interface PlatePlan {
  /** The text as it will actually be drawn — possibly ellipsized. */
  text: string;
  /** World size of the plate, metres. */
  widthM: number;
  heightM: number;
  /** Size of this plate's cell in the atlas, pixels. */
  cellW: number;
  cellH: number;
}

/**
 * Sizes one plate from its text. `measure` returns the width in atlas pixels at
 * `styleFont(style)` — a canvas context in the renderer, a stub in tests.
 */
export function planPlate(
  text: string,
  style: SignStyle,
  measure: (s: string) => number,
): PlatePlan {
  const ppm = pxPerMetre(style);
  const minCellW = Math.round(style.minWidthM * ppm);
  const maxCellW = Math.round(style.maxWidthM * ppm);
  const maxTextPx = maxCellW - style.padPx * 2;

  const drawn = ellipsize(text, maxTextPx, measure);
  const textPx = measure(drawn);

  let cellW = Math.ceil(textPx + style.padPx * 2);
  if (cellW < minCellW) cellW = minCellW;
  if (cellW > maxCellW) cellW = maxCellW;

  return {
    text: drawn,
    widthM: cellW / ppm,
    heightM: style.plateHeightM,
    cellW,
    cellH: style.cellPx,
  };
}

/**
 * Identity of a plate's APPEARANCE: same text and style share one atlas cell.
 */
export function signKey(kind: SignKind, text: string, poiKind?: string): string {
  return `${kind} ${poiKind ?? ""} ${text}`;
}

// ---------------------------------------------------------------------------
// Atlas packing
// ---------------------------------------------------------------------------

export interface AtlasItem {
  key: string;
  w: number;
  h: number;
}

export interface AtlasCell extends AtlasItem {
  page: number;
  x: number;
  y: number;
}

export interface AtlasPage {
  width: number;
  height: number;
}

export interface PackedAtlas {
  pages: AtlasPage[];
  cells: AtlasCell[];
}

/**
 * Gutter around every cell, atlas pixels. The atlas is mipmapped and mipping
 * averages across cell boundaries; the renderer floods each gutter with the
 * cell's own background so what bleeds in is the plate's enamel, not the next
 * street's lettering.
 */
export const ATLAS_GUTTER_PX = 8;

/** Hard cap on a page's dimensions — beyond this we start another page. */
export const ATLAS_MAX_PX = 2048;

/**
 * Shelf-packs plate cells into as few pages as possible, tallest first; pages
 * are trimmed to the next power of two. Deterministic: fully ordered sort
 * (height, width, key).
 */
export function packAtlas(
  items: readonly AtlasItem[],
  maxSize: number = ATLAS_MAX_PX,
  gutter: number = ATLAS_GUTTER_PX,
): PackedAtlas {
  const pages: AtlasPage[] = [];
  const cells: AtlasCell[] = [];
  if (items.length === 0) return { pages, cells };

  const sorted = [...items].sort(
    (a, b) =>
      b.h - a.h ||
      b.w - a.w ||
      (a.key < b.key ? -1 : a.key > b.key ? 1 : 0),
  );

  const limit = Math.max(1, maxSize - gutter * 2);
  let page = 0;
  let cursorX = gutter;
  let cursorY = gutter;
  let shelfH = 0;
  let maxRight = 0;

  const closePage = (): void => {
    pages.push({
      width: Math.min(maxSize, nextPow2(maxRight + gutter)),
      height: Math.min(maxSize, nextPow2(cursorY + shelfH + gutter)),
    });
  };

  for (const item of sorted) {
    const w = Math.min(item.w, limit);
    const h = Math.min(item.h, limit);

    if (cursorX + w + gutter > maxSize && cursorX > gutter) {
      cursorY += shelfH + gutter;
      cursorX = gutter;
      shelfH = 0;
    }
    if (cursorY + h + gutter > maxSize && cells.length > 0) {
      closePage();
      page += 1;
      cursorX = gutter;
      cursorY = gutter;
      shelfH = 0;
      maxRight = 0;
    }

    cells.push({ key: item.key, w, h, page, x: cursorX, y: cursorY });
    cursorX += w + gutter;
    if (cursorX - gutter > maxRight) maxRight = cursorX - gutter;
    if (h > shelfH) shelfH = h;
  }
  closePage();

  return { pages, cells };
}

/** Smallest power of two >= `n`, floored at 16 (a 4×4 texture helps nobody). */
export function nextPow2(n: number): number {
  let p = 16;
  while (p < n) p *= 2;
  return p;
}
